import React, { useEffect, useRef } from 'react';
import { Info, Star, ExternalLink } from 'lucide-react';
import {
  KPICard, DomainCard, StatusBadge, SectionHeading,
  NNLineChartWidget, DataFreshnessChip, EmptyState, Skeleton,
  Button, Tooltip, TooltipTrigger, TooltipContent, ICON_PX, ICON_STROKE, useToast,
} from '../ui';
import { useConsumerUnit } from '../../contexts/ConsumerUnitContext';
import { mockConsumerReputation } from '../../utils/mockData';
import { addToPriorityQueue } from '../../utils/api';

function Stars({ count, max = 5 }) {
  return (
    <span className="inline-flex gap-0.5">
      {Array.from({ length: max }, (_, i) => (
        <Star key={i} size={ICON_PX.sm} fill={i < count ? 'var(--orange-warn)' : 'none'} stroke="var(--orange-warn)" strokeWidth={ICON_STROKE} />
      ))}
    </span>
  );
}

function ConsumerReputationTab({ readOnly }) {
  const { selectedUnitId } = useConsumerUnit();
  const data = mockConsumerReputation[selectedUnitId] || mockConsumerReputation.all;
  const { toast } = useToast();
  const queuedRef = useRef(false);

  useEffect(() => {
    if (readOnly || queuedRef.current || !data.reviews) return;
    const flagged = data.reviews.filter((r) => !r.responded && r.stars <= 2 && r.daysUnanswered > 7);
    if (flagged.length === 0) return;
    queuedRef.current = true;
    flagged.forEach((r) => {
      addToPriorityQueue({
        title: `Unanswered ${r.stars}-star review: "${r.text.slice(0, 50)}..."`,
        priority: 'medium', category: 'Consumer', type: 'task',
        estimatedTime: 20, dueDate: new Date().toISOString(),
      }).catch(() => {});
    });
    toast({ title: `${flagged.length} unanswered review(s) added to Priority Queue` });
  }, [data.reviews, readOnly, toast]);

  if (!data) return <Skeleton.KPICard size="md" />;

  const volumeChange = data.volumePrior30 > 0
    ? Math.round(((data.volume30d - data.volumePrior30) / data.volumePrior30) * 100)
    : 0;

  const trendData = data.trend.map((t) => ({ x: t.period, rating: t.value }));

  return (
    <div className="flex flex-col gap-3">
      <div>
        <SectionHeading
          size="sm" className="mb-1.5" title="Google Rating"
          action={
            <div className="flex items-center gap-2">
              <DataFreshnessChip label={`Synced ${new Date(data.lastSync).toLocaleDateString()}`} status="stale" />
              <Tooltip>
                <TooltipTrigger asChild>
                  <button type="button" className="flex items-center text-text-muted hover:text-text-dark-secondary transition-colors"><Info size={ICON_PX.md} /></button>
                </TooltipTrigger>
                <TooltipContent side="bottom" maxWidth={220}>Google My Business rating data, refreshed every 4 hours.</TooltipContent>
              </Tooltip>
            </div>
          }
        />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
          <KPICard size="sm" density="compact" accentPlacement="top" domain="consumer"
            label="Avg Rating"
            value={<span className="flex items-center gap-1">{data.avgRating} <Stars count={Math.round(data.avgRating)} /></span>}
            sublabel={`${data.totalReviews.toLocaleString()} reviews`} />
          <KPICard size="sm" density="compact" accentPlacement="top" domain="consumer"
            label="Last 30d Volume" value={data.volume30d}
            sublabel={`Prior 30d: ${data.volumePrior30}`} />
          <KPICard size="sm" density="compact" accentPlacement="top" domain="consumer"
            label="Volume Change" value={`${volumeChange > 0 ? '+' : ''}${volumeChange}%`} />
        </div>
      </div>

      <div>
        <SectionHeading
          size="sm" className="mb-1.5" title="Rating Trend"
          action={
            <Tooltip>
              <TooltipTrigger asChild>
                <button type="button" className="flex items-center text-text-muted hover:text-text-dark-secondary transition-colors"><Info size={ICON_PX.md} /></button>
              </TooltipTrigger>
              <TooltipContent side="bottom" maxWidth={220}>Google rating trend over 90, 60, and 30-day windows.</TooltipContent>
            </Tooltip>
          }
        />
        <DomainCard domain="consumer" accentPlacement="top">
          <NNLineChartWidget
            data={trendData}
            xKey="x"
            series={[{ key: 'rating', name: 'Rating', color: '#E07C3E' }]}
            height={180}
          />
        </DomainCard>
      </div>

      <div>
        <SectionHeading
          size="sm" className="mb-1.5" title="Recent Reviews"
          action={
            <div className="flex items-center gap-2">
              {data.gmbDeepLink && (
                <Button variant="ghost" size="sm" asChild>
                  <a href={data.gmbDeepLink} target="_blank" rel="noopener noreferrer">
                    Open in GMB <ExternalLink size={ICON_PX.sm} className="ml-1" />
                  </a>
                </Button>
              )}
              <Tooltip>
                <TooltipTrigger asChild>
                  <button type="button" className="flex items-center text-text-muted hover:text-text-dark-secondary transition-colors"><Info size={ICON_PX.md} /></button>
                </TooltipTrigger>
                <TooltipContent side="bottom" maxWidth={220}>Last 5 Google reviews. Unanswered low-rating reviews are flagged for action.</TooltipContent>
              </Tooltip>
            </div>
          }
        />
        {(!data.reviews || data.reviews.length === 0) ? (
          <EmptyState title="No reviews" body="No Google reviews found for this unit." />
        ) : (
          <div className="flex flex-col gap-2">
            {data.reviews.map((r) => {
              const isUnansweredLow = !r.responded && r.stars <= 2;
              return (
                <DomainCard key={r.id} domain="consumer" accentPlacement="left" alertLevel={isUnansweredLow ? 'critical' : undefined}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Stars count={r.stars} />
                      <span className="text-caption text-text-muted">{r.date}</span>
                      {selectedUnitId === 'all' && <span className="text-caption text-text-muted">· {r.unitName}</span>}
                    </div>
                    {!r.responded ? (
                      <StatusBadge variant={isUnansweredLow ? 'critical' : 'watch'}>
                        UNANSWERED{r.daysUnanswered > 0 ? ` (${r.daysUnanswered}d)` : ''}
                      </StatusBadge>
                    ) : (
                      <StatusBadge variant="on-track">Responded</StatusBadge>
                    )}
                  </div>
                  <p className="text-body text-text-dark mt-1">{r.text}</p>
                </DomainCard>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

export default ConsumerReputationTab;
